import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import type { BufferedChildContext } from "./SettingsDetailPage";
import { TestCodeDetailTabs } from "./TestCodeDetailTabs";

const rowsLabel = (n: number) => `${n} row${n === 1 ? "" : "s"}`;
import { markUserEdit } from "../../hooks/useUnsavedGuard";
import { AiCombo } from "../../components/AiCombo";

/**
 * Test Code → "Copy from an existing test" — the panel under General Information
 * on the NEW form (Test Code prototype v12).
 *
 * Pick a source test (Matrix → Test → Method, cascading as the prototype does),
 * tick which of the seven sections to bring across, and Save creates the test
 * AND copies in ONE request: the panel registers a {@link BufferedChildHandle}
 * whose payload is merged into the create POST, so a user can never end up with
 * a saved-but-empty test because a second call failed.
 *
 * ADDITIVE (owner decision 2026-10-13): a row already on the target is left
 * exactly as it is, and retired rows are never copied. This deliberately
 * differs from legacy, which DELETES the target's rows of that category and
 * reinserts from the source — same button, different outcome. The rule lives in
 * `AI_TestMethod_CopySettings_SP`; this panel only chooses what to send.
 *
 * Shown only while the record is NEW. Copying into an EXISTING test is the
 * detail-view "Copy Parameters" menu, which posts to
 * `POST /api/v1/test-methods/{id}/copy` instead — not built yet.
 */

/** The seven sections, in the prototype's order. "Title" in the prototype
 *  screenshot is the select-all checkbox label, NOT a section. Keys are what
 *  `AI_TestMethod_CopySettings_SP` accepts; it rejects anything else (50444). */
export const COPY_SECTIONS: ReadonlyArray<readonly [string, string]> = [
  ["PrepMethod", "Prep Method"],
  ["QCType", "QC Type"],
  ["TestGuide", "Test Guide"],
  ["SampleParameters", "Sample Parameters"],
  ["Surrogates", "Surrogates"],
  ["InternalStandard", "Internal Standard"],
  ["QCLimits", "QC Limits (QC Sample Parameters)"],
] as const;

type Opt = { id: string; name: string };
type TestRow = { oid: string; test: string; matrix: string; method: string; isGroup?: string };

/** The list column is the display text 'Yes' / 'No', not a bit. */
const isGroupRow = (r: TestRow) => String(r.isGroup ?? "").toLowerCase() === "yes";

/** What "Copy Test" hands the New form: the source to copy from, already
 *  chosen. The panel then opens ON with every section ticked, so Save alone
 *  creates the copy — the prototype's flow (openNew(src)). */
export type CopyPrefill = {
  oid: string;
  matrix?: string;
  test?: string;
  /** The source is a GROUP test, so this is legacy's "Copy Test Group": the
   *  panel copies MEMBERS, offers only group tests, and drops the seven section
   *  ticks (a group test has none of those rows). */
  group?: boolean;
} | null;

export function TestCodeCopyFromPanel({ ctx, prefill }: { ctx: BufferedChildContext; prefill?: CopyPrefill }) {
  /* "Copy Test Group" (legacy Testparameter_DetailView_CopyTest, the layout
     group at Model.xafml:69764): Matrix + Test only, over GROUP tests, and the
     one section is the members. The form's Is Group box decides it, so the
     panel always matches the kind of test being created. Declared up here
     because the Save-flow registration below reads it. */
  const groupMode = !!ctx.values?.isGroup;
  const [on, setOn] = useState(!!prefill);
  const [rows, setRows] = useState<TestRow[]>([]);
  const [matrices, setMatrices] = useState<Opt[]>([]);
  const [matrix, setMatrix] = useState(prefill?.matrix ?? "");
  const [testName, setTestName] = useState(prefill?.test ?? "");
  const [sourceOid, setSourceOid] = useState(prefill?.oid ?? "");
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(COPY_SECTIONS.map(([k]) => k)),
  );
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);

  // Apply the prefill whenever it ARRIVES, not only on first mount.
  //
  // Clicking "Copy Test" on a saved record switches ctx.id from that record to
  // null, which re-renders THIS SAME component instance — it does not remount
  // it. useState initialisers run once per instance, so the three values above
  // kept the "" they were born with and the Copy From boxes came up empty while
  // the panel sat open with every section ticked. An effect keyed on the prefill
  // is what actually tracks it.

  // Row counts for the chosen source — the "9 rows" beside each tick, so the
  // user sees what they are about to copy before copying it (prototype v12).
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    if (!sourceOid) {
      setCounts(null);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(`/api/v1/test-methods/${sourceOid}/section-counts`);
        if (!alive) return;
        const n: Record<string, number> | null = data?.data ?? null;
        setCounts(n);
        // A section with NO rows starts UNTICKED (owner request 2026-10-28):
        // ticking "Surrogates 0 rows" copies nothing, so offering it pre-ticked
        // only invites the user to confirm an empty action. Runs ONLY when the
        // SOURCE changes - the same moment the ticks are reset to all - so it
        // never overrides a choice the user has since made. A failed counts
        // call leaves every tick on, as before.
        if (n) setPicked(new Set(COPY_SECTIONS.filter(([k]) => (n[k] ?? 0) > 0).map(([k]) => k)));
      } catch {
        if (alive) setCounts(null); // the ticks still work; only the hint is missing
      }
    })();
    return () => {
      alive = false;
    };
  }, [sourceOid]);

  // A source chosen for the other kind of test cannot be used for this one.
  const prevGroupMode = useRef(groupMode);
  useEffect(() => {
    if (prevGroupMode.current === groupMode) return;
    prevGroupMode.current = groupMode;
    setMatrix("");
    setTestName("");
    setSourceOid("");
    setPicked(new Set(COPY_SECTIONS.map(([k]) => k)));
  }, [groupMode]);

  const prefillOid = prefill?.oid ?? null;
  useEffect(() => {
    if (!prefill) {
      // A plain New after a Copy Test: the same instance would otherwise keep
      // the previous source, so the next blank form is actually blank.
      setOn(false);
      setMatrix("");
      setTestName("");
      setSourceOid("");
      return;
    }
    setOn(true);
    setMatrix(prefill.matrix ?? "");
    setTestName(prefill.test ?? "");
    setSourceOid(prefill.oid);
    setPicked(new Set(COPY_SECTIONS.map(([k]) => k)));
    // Keyed on the source id, not the object: the host rebuilds the prefill
    // object on every render, and depending on the object would re-run this on
    // each one and fight the user's own edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillOid]);

  // The source list is every live test; the cascade below filters it in memory
  // rather than per-keystroke round trips. 165 rows on the reference database,
  // so one fetch is cheaper than three dependent endpoints.
  useEffect(() => {
    if (!on || loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    void (async () => {
      try {
        const [list, mx] = await Promise.all([
          api.get("/api/v1/modules/set-testcode", { params: { pageSize: 1000 } }),
          api.get("/api/v1/test-methods/matrices"),
        ]);
        const data = list.data?.data ?? list.data?.rows ?? [];
        setRows(Array.isArray(data) ? data : []);
        const mxd = mx.data?.data ?? mx.data ?? [];
        setMatrices(Array.isArray(mxd) ? mxd : []);
      } finally {
        setLoading(false);
      }
    })();
  }, [on]);

  const testsForMatrix = useMemo(() => {
    const seen = new Set<string>();
    return rows
      // Only a test of the same kind can be the source. Legacy finds a group
      // source with "[IsGroup] = True" (TestParameterViewControllercs.cs:1215);
      // a group test has none of the seven sections a normal test copies.
      .filter((r) => isGroupRow(r) === groupMode)
      .filter((r) => !matrix || r.matrix === matrix)
      .filter((r) => (seen.has(r.test) ? false : (seen.add(r.test), true)))
      .sort((a, b) => a.test.localeCompare(b.test));
  }, [rows, matrix, groupMode]);

  const methodsForTest = useMemo(
    () =>
      rows
        .filter((r) => (!matrix || r.matrix === matrix) && r.test === testName && isGroupRow(r) === groupMode)
        .sort((a, b) => (a.method || "").localeCompare(b.method || "")),
    [rows, matrix, testName, groupMode],
  );

  // Register with the Save flow. validate() runs BEFORE the confirm, so a
  // half-filled panel stops the save instead of creating a test and then
  // failing the copy.
  useEffect(() => {
    ctx.handleRef.current = {
      validate: () => {
        // A saved record has no copy to validate. Without this, an earlier
        // Copy Test left the panel armed and blocked that record's Save.
        if (ctx.id || !on) return null;
        if (!sourceOid)
          return groupMode
            ? "Copy Group Test: choose the Matrix and Test to copy from."
            : "Copy From: choose the Matrix, Test and Method to copy from.";
        if (!groupMode && picked.size === 0)
          return "Copy Settings: tick at least one section to copy.";
        return null;
      },
      payload: () =>
        !ctx.id && on && sourceOid && (groupMode || picked.size)
          ? { copyFrom: { sourceOid, sections: groupMode ? ["GroupTest"] : [...picked] } }
          : {},
    };
    return () => {
      ctx.handleRef.current = null;
    };
  }, [ctx.handleRef, ctx.id, on, sourceOid, picked, groupMode]);

  // A SAVED record shows its children instead: Method Defaults + Test Parameter
  // Defaults. Copying into a saved test is the detail-view "Copy Parameters"
  // menu (POST {id}/copy), not this panel.
  // ctx.viewOnly is passed ON: a row click opens the record read-only, and the
  // tabs have to honour that too. Without it the header fields were read-only
  // while Add / Remove / Delete and inline cell editing stayed live below, so
  // clicking a row to LOOK at a test could change it.
  if (ctx.id) {
    return (
      <TestCodeDetailTabs
        id={ctx.id}
        testName={ctx.recordName}
        viewOnly={ctx.viewOnly}
        reloadToken={ctx.reloadToken}
        onGuideRowsChange={(has) => ctx.patchRecord({ hasTestGuideRows: has })}
      />
    );
  }
  if (ctx.viewOnly) return null;

  const touch = (el?: EventTarget | null) => {
    ctx.onDirty();
    if (el instanceof HTMLElement) markUserEdit(el);
  };
  // The sections worth ticking: those with rows. Until the counts arrive (or if
  // the call failed) every section counts as copyable, so the panel behaves as
  // it always did rather than briefly showing everything unticked.
  const copyable = COPY_SECTIONS.filter(([k]) => !counts || (counts[k] ?? 0) > 0);
  // "All" means every section that HAS rows - with the empty ones unticked by
  // design, requiring literally all of them would leave this permanently off.
  const allOn = copyable.length > 0 && copyable.every(([k]) => picked.has(k));

  return (
    <div className="sd-section tc-copy">
      <label className="tc-copy-toggle">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => {
            setOn(e.target.checked);
            touch(e.target);
          }}
        />
        <b>{groupMode ? "Copy the members of another group test" : "Copy from an existing test"}</b>
      </label>

      {on && (
        <div className="tc-copy-body">
          <div className="tc-copy-from">
            <div className="tc-copy-h">{groupMode ? "Copy Group Test" : "Copy From"}</div>
            <div className="tc-copy-grid">
              {/* The SHARED AiCombo, the same control General Information above
                  uses. These were native <select>s, which render with the
                  browser's own chrome and the stylesheet's yellow default
                  background, so the panel did not look like the form it sits in
                  (owner 2026-10-14: always use the standard format). */}
              <label className="tc-copy-lbl">Matrix<span className="req">*</span></label>
              <AiCombo
                value={matrix || null}
                options={matrices.map((m) => ({ value: m.name, label: m.name }))}
                placeholder="— Select —"
                onChange={(v) => {
                  setMatrix(v ?? "");
                  setTestName("");
                  setSourceOid("");
                  touch();
                }}
              />

              <label className="tc-copy-lbl">Test<span className="req">*</span></label>
              <AiCombo
                value={testName || null}
                options={testsForMatrix.map((r) => ({ value: r.test, label: r.test }))}
                placeholder={loading ? "Loading…" : "— Select —"}
                disabled={loading}
                className={testName ? undefined : "required-empty"}
                onChange={(v) => {
                  setTestName(v ?? "");
                  setSourceOid(
                    groupMode
                      ? (testsForMatrix.find((r) => r.test === v)?.oid ?? "")
                      : "",
                  );
                  touch();
                }}
              />

              {!groupMode && (
                <>
              <label className="tc-copy-lbl">Method<span className="req">*</span></label>
              <AiCombo
                value={sourceOid || null}
                options={methodsForTest.map((r) => ({ value: r.oid, label: r.method || "(no method)" }))}
                placeholder="— Select —"
                disabled={!testName}
                className={sourceOid ? undefined : "required-empty"}
                onChange={(v) => {
                  setSourceOid(v ?? "");
                  touch();
                }}
              />
                </>
              )}
            </div>
          </div>

          {groupMode ? (
            <div className="tc-copy-set">
              <div className="tc-copy-h">Copy Settings</div>
              <div className="tc-copy-list">
                <label>
                  <input type="checkbox" checked readOnly />
                  Group Test
                  {counts && <span className="tc-copy-rows">{rowsLabel(counts.GroupTest ?? 0)}</span>}
                </label>
              </div>
            </div>
          ) : (
          <div className="tc-copy-set">
            <div className="tc-copy-h">Copy Settings</div>
            <div className="tc-copy-list">
              <label className="tc-copy-all">
                <input
                  type="checkbox"
                  checked={allOn}
                  onChange={(e) => {
                    // Ticking All never turns on an EMPTY section.
                    setPicked(
                      e.target.checked ? new Set(copyable.map(([k]) => k)) : new Set(),
                    );
                    touch(e.target);
                  }}
                />
                <b>All sections</b>
              </label>
              {COPY_SECTIONS.map(([key, label]) => (
                <label key={key}>
                  <input
                    type="checkbox"
                    checked={picked.has(key)}
                    onChange={(e) => {
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(key);
                        else next.delete(key);
                        return next;
                      });
                      touch(e.target);
                    }}
                  />
                  {label}
                  {/* How many rows that section holds on the SOURCE — the
                      prototype's "9 rows". Shown only once a source is chosen,
                      because before that there is nothing to count. */}
                  {counts && <span className="tc-copy-rows">{rowsLabel(counts[key] ?? 0)}</span>}
                </label>
              ))}
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}
